import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AddonService,
  AddonServiceCreateInput,
  AddonServiceUpdateInput,
  CeramicPackageDef,
  CeramicPackageTier,
  CeramicPackageTierPutInput,
  LegacyPricingArchiveResponse,
  PanelOverridePutInput,
  PanelPricingCell,
  PpfMaterialTier,
  PpfMaterialTierPutInput,
  PpfZoneDef,
  PpfZonePriceOverride,
  PpfZonePriceOverridePutInput,
  ServiceCategoryMinimum,
  ServiceCategoryMinimumPutInput,
  ShopRatesPutInput,
  ShopRatesResponse,
  TintPriceMatrixCell,
  TintPriceMatrixCellPutInput,
  TintZoneDef,
  VehicleDisassemblyOverridePutInput,
  VehiclePanelOverridePutInput,
  VehiclePanelPricingCell,
  VehicleRefSource,
  VehicleServiceKind,
  VehicleServiceQuantities,
  VehicleServiceQuantityPutInput,
  VehiclesPanelQuery,
  VehiclesPanelResponse,
} from '@wrapforce/shared/schemas/pricing';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';

export function useShopRates(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'shop-rates'],
    queryFn: () => api.get<ShopRatesResponse>(workspacePath(workspaceId!, '/shop-rates')),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });
}

export function useUpdateShopRates(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ShopRatesPutInput) => api.put<ShopRatesResponse>(workspacePath(workspaceId!, '/shop-rates'), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'shop-rates'] });
    },
  });
}

// ---------------------------------------------------------------------------
// §32.2 Tab 2 (PPF) - material tiers / zone price overrides / zone defs.
// ---------------------------------------------------------------------------

export function usePpfMaterialTiers(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'ppf-material-tiers'],
    queryFn: () => api.get<PpfMaterialTier[]>(workspacePath(workspaceId!, '/ppf-material-tiers')),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });
}

export function useUpdatePpfMaterialTier(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ filmTier, input }: { filmTier: PpfMaterialTier['filmTier']; input: PpfMaterialTierPutInput }) =>
      api.put<PpfMaterialTier>(workspacePath(workspaceId!, `/ppf-material-tiers/${filmTier}`), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'ppf-material-tiers'] });
    },
  });
}

export function usePpfZoneOverrides(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'ppf-zone-overrides'],
    queryFn: () => api.get<PpfZonePriceOverride[]>(workspacePath(workspaceId!, '/ppf-zone-overrides')),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });
}

export function useUpdatePpfZoneOverride(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ zoneKey, sizeClass, input }: { zoneKey: PpfZonePriceOverride['zoneKey']; sizeClass: PpfZonePriceOverride['sizeClass']; input: PpfZonePriceOverridePutInput }) =>
      api.put<PpfZonePriceOverride>(workspacePath(workspaceId!, `/ppf-zone-overrides/${zoneKey}/${sizeClass}`), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'ppf-zone-overrides'] });
    },
  });
}

// Platform-wide, not workspace-scoped (no :workspaceId in the route) - longer
// staleTime than the workspace-scoped hooks above since this data never
// changes per-workspace. workspaceId stays in the query key only for cache
// namespacing consistency with the rest of this feature's hooks (mirrors
// useVehicleCatalog's rationale in quotes/pricing/pricingApi.ts).
export function usePpfZoneDefs(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'ppf-zone-defs'],
    queryFn: () => api.get<PpfZoneDef[]>('/api/v1/ppf-zone-defs'),
    staleTime: 30 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Wrap partial-panel pricing (panel_defs + workspace_panel_overrides).
// ---------------------------------------------------------------------------

export function usePanelPricingMatrix(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'panel-pricing'],
    queryFn: () => api.get<PanelPricingCell[]>(workspacePath(workspaceId!, '/panel-pricing')),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });
}

export function useUpdatePanelOverride(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ panelKey, sizeClass, input }: { panelKey: PanelPricingCell['panelKey']; sizeClass: PanelPricingCell['sizeClass']; input: PanelOverridePutInput }) =>
      api.put<PanelPricingCell>(workspacePath(workspaceId!, `/panel-overrides/${panelKey}/${sizeClass}`), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'panel-pricing'] });
    },
  });
}

export function useDeletePanelOverride(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ panelKey, sizeClass }: { panelKey: PanelPricingCell['panelKey']; sizeClass: PanelPricingCell['sizeClass'] }) =>
      api.delete<void>(workspacePath(workspaceId!, `/panel-overrides/${panelKey}/${sizeClass}`)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'panel-pricing'] });
    },
  });
}

// ---------------------------------------------------------------------------
// §32.2 Tab 3 (Tint) - price matrix / zone defs.
// ---------------------------------------------------------------------------

export function useTintPriceMatrix(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'tint-price-matrix'],
    queryFn: () => api.get<TintPriceMatrixCell[]>(workspacePath(workspaceId!, '/tint-price-matrix')),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });
}

export function useUpdateTintPriceMatrixCell(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      zoneKey, filmGrade, sizeBucket, input,
    }: {
      zoneKey: TintPriceMatrixCell['zoneKey'];
      filmGrade: TintPriceMatrixCell['filmGrade'];
      sizeBucket: TintPriceMatrixCell['sizeBucket'];
      input: TintPriceMatrixCellPutInput;
    }) => api.put<TintPriceMatrixCell>(workspacePath(workspaceId!, `/tint-price-matrix/${zoneKey}/${filmGrade}/${sizeBucket}`), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'tint-price-matrix'] });
    },
  });
}

// Platform-wide, not workspace-scoped - longer staleTime, mirrors usePpfZoneDefs.
export function useTintZoneDefs(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'tint-zone-defs'],
    queryFn: () => api.get<TintZoneDef[]>('/api/v1/tint-zone-defs'),
    staleTime: 30 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// §32.2 Tab 4 (Ceramic) - package tiers / package defs.
// ---------------------------------------------------------------------------

export function useCeramicPackageTiers(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'ceramic-package-tiers'],
    queryFn: () => api.get<CeramicPackageTier[]>(workspacePath(workspaceId!, '/ceramic-package-tiers')),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });
}

export function useUpdateCeramicPackageTier(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      packageKey, sizeBucket, input,
    }: {
      packageKey: CeramicPackageTier['packageKey'];
      sizeBucket: CeramicPackageTier['sizeBucket'];
      input: CeramicPackageTierPutInput;
    }) => api.put<CeramicPackageTier>(workspacePath(workspaceId!, `/ceramic-package-tiers/${packageKey}/${sizeBucket}`), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'ceramic-package-tiers'] });
    },
  });
}

// Platform-wide, not workspace-scoped - longer staleTime, mirrors usePpfZoneDefs.
export function useCeramicPackageDefs(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'ceramic-package-defs'],
    queryFn: () => api.get<CeramicPackageDef[]>('/api/v1/ceramic-package-defs'),
    staleTime: 30 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// §32 Phase 3b - legacy pricing archive banner. Lives here (not a new file)
// to reuse this module's existing api/workspacePath setup - imported from
// PricingPage.tsx directly, which always renders.
// ---------------------------------------------------------------------------

export function useLegacyPricingArchive(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'legacy-pricing-archive'],
    queryFn: () => api.get<LegacyPricingArchiveResponse>(workspacePath(workspaceId!, '/legacy-pricing-archive')),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });
}

export function useDismissLegacyPricingBanner(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>(workspacePath(workspaceId!, '/legacy-pricing-archive/dismiss')),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'legacy-pricing-archive'] });
    },
  });
}

// ---------------------------------------------------------------------------
// §34.1 - Settings Vehicles tab: merged, paginated, searchable panel over the
// platform catalog + this workspace's own custom vehicles/overrides.
// ---------------------------------------------------------------------------

export function useVehiclesPanel(workspaceId: string | null, query: VehiclesPanelQuery) {
  return useQuery({
    queryKey: [workspaceId, 'vehicles-panel', query],
    queryFn: () => api.get<VehiclesPanelResponse>(workspacePath(workspaceId!, '/vehicles-panel'), { query }),
    enabled: !!workspaceId,
    placeholderData: keepPreviousData,
  });
}

// Catalog and overridden rows share vehicle_catalog's id space - this PUT
// upserts a workspace override on top of the catalog default, turning a
// 'catalog' row into 'overridden' (D-34-04's merged panel).
export function usePutVehicleDisassemblyOverride(workspaceId: string | null) {
  return useMutation({
    mutationFn: ({ vehicleId, input }: { vehicleId: string; input: VehicleDisassemblyOverridePutInput }) =>
      api.put<void>(workspacePath(workspaceId!, `/vehicle-disassembly-overrides/${vehicleId}`), input),
  });
}

// Resets an 'overridden' row back to the catalog default.
export function useDeleteVehicleDisassemblyOverride(workspaceId: string | null) {
  return useMutation({
    mutationFn: (vehicleId: string) => api.delete<void>(workspacePath(workspaceId!, `/vehicle-disassembly-overrides/${vehicleId}`)),
  });
}

// ---------------------------------------------------------------------------
// §36 - Labour & Policies tab: add-on services and per-service-category
// minimums.
// ---------------------------------------------------------------------------

export function useAddonServices(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'addon-services'],
    queryFn: () => api.get<AddonService[]>(workspacePath(workspaceId!, '/addon-services')),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });
}

export function useCreateAddonService(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddonServiceCreateInput) => api.post<AddonService>(workspacePath(workspaceId!, '/addon-services'), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'addon-services'] });
    },
  });
}

export function useUpdateAddonService(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AddonServiceUpdateInput }) =>
      api.patch<AddonService>(workspacePath(workspaceId!, `/addon-services/${id}`), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'addon-services'] });
    },
  });
}

export function useDeleteAddonService(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(workspacePath(workspaceId!, `/addon-services/${id}`)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'addon-services'] });
    },
  });
}

export function useServiceCategoryMinimums(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'service-category-minimums'],
    queryFn: () => api.get<ServiceCategoryMinimum[]>(workspacePath(workspaceId!, '/service-category-minimums')),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });
}

export function useUpsertServiceCategoryMinimum(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      category, input,
    }: {
      category: ServiceCategoryMinimum['serviceCategory'];
      input: ServiceCategoryMinimumPutInput;
    }) => api.put<ServiceCategoryMinimum>(workspacePath(workspaceId!, `/service-category-minimums/${category}`), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'service-category-minimums'] });
    },
  });
}

// ---------------------------------------------------------------------------
// §36 - per-vehicle pricing overrides (panel pricing + service product
// prices), scoped by source ('catalog' | 'workspace') + vehicleId.
// ---------------------------------------------------------------------------

export function useVehiclePanelPricing(workspaceId: string | null, source: VehicleRefSource | null, vehicleId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'vehicle-panel-pricing', source, vehicleId],
    queryFn: () => api.get<VehiclePanelPricingCell[]>(workspacePath(workspaceId!, `/vehicle-pricing/${source}/${vehicleId}/panel-pricing`)),
    enabled: !!workspaceId && !!source && !!vehicleId,
    staleTime: 30_000,
  });
}

export function usePutVehiclePanelOverride(workspaceId: string | null, source: VehicleRefSource | null, vehicleId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ panelKey, input }: { panelKey: VehiclePanelPricingCell['panelKey']; input: VehiclePanelOverridePutInput }) =>
      api.put<void>(workspacePath(workspaceId!, `/vehicle-pricing/${source}/${vehicleId}/panel-overrides/${panelKey}`), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'vehicle-panel-pricing', source, vehicleId] });
    },
  });
}

export function useDeleteVehiclePanelOverride(workspaceId: string | null, source: VehicleRefSource | null, vehicleId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (panelKey: VehiclePanelPricingCell['panelKey']) =>
      api.delete<void>(workspacePath(workspaceId!, `/vehicle-pricing/${source}/${vehicleId}/panel-overrides/${panelKey}`)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'vehicle-panel-pricing', source, vehicleId] });
    },
  });
}

// ---------------------------------------------------------------------------
// §37 - per-vehicle service material quantities (replaces the per-vehicle
// service-product price overrides above with a single "how much material
// does this vehicle need" number per kind).
// ---------------------------------------------------------------------------

export function useVehicleServiceQuantities(workspaceId: string | null, source: VehicleRefSource | null, vehicleId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'vehicle-service-quantities', source, vehicleId],
    queryFn: () => api.get<VehicleServiceQuantities>(workspacePath(workspaceId!, `/vehicle-pricing/${source}/${vehicleId}/service-quantities`)),
    enabled: !!workspaceId && !!source && !!vehicleId,
    staleTime: 30_000,
  });
}

export function usePutVehicleServiceQuantity(workspaceId: string | null, source: VehicleRefSource | null, vehicleId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, input }: { kind: VehicleServiceKind; input: VehicleServiceQuantityPutInput }) =>
      api.put<void>(workspacePath(workspaceId!, `/vehicle-pricing/${source}/${vehicleId}/service-quantities/${kind}`), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'vehicle-service-quantities', source, vehicleId] });
    },
  });
}

export function useDeleteVehicleServiceQuantity(workspaceId: string | null, source: VehicleRefSource | null, vehicleId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (kind: VehicleServiceKind) =>
      api.delete<void>(workspacePath(workspaceId!, `/vehicle-pricing/${source}/${vehicleId}/service-quantities/${kind}`)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [workspaceId, 'vehicle-service-quantities', source, vehicleId] });
    },
  });
}
